import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, Result } from "antd";
import DashboardLayout from "./layouts/DashboardLayout";
import Login from "./pages/Login/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import AdminLayout from "./layouts/AdminLayout";
import AdminPage from "./pages/Admin/Admin";
import Medicalicd from "./pages/Medicalicd/Medicalicd";
import MedicalActivity from "./pages/medicalActivity/medicalActivity";
import TpaPayerPage from "./pages/Levels/TpaPayer";
import NetworkPlanPage from "./pages/Levels/Networkplan";
import PolicyPage from "./pages/Levels/PolicyPage";
import SpecialtyPage from "./pages/Levels/SpecialtyPage";
import DoctorPage from "./pages/Levels/DoctorPage";
import CustomActivity from "./pages/customActivity/customActivity";
import CustomIcd from "./pages/customIcd/customIcd";
import ICDAgeGender from "./pages/icd-config/IcdAgeGender/IcdAgeGender";
import IcdNotCovered from "./pages/icd-config/IcdNotCovered/IcdNotCovered";
import IcdNotAllowedPrincipalDiagnosis from "./pages/icd-config/IcdNotAllowedPrincipalDiagnosis/IcdNotAllowedPrincipalDiagnosis";
import ActivityNotCovered from "./pages/activity-config/ActivityNotCovered/ActivityNotCovered";
import ActivityAgeGender from "./pages/activity-config/AgeGender/ActivityAgeGender";
import ActivitiesNotAllowed from "./pages/activity-config/ActivitiesNotAllowed/ActivitiesNotAllowed";
import ActivitiesAllowed from "./pages/activity-config/ActivitiesAllowed/ActivitiesAllowed";
import ActivityPreApprovals from "./pages/activity-config/Pre-Approvals/Pre-Approvals";
import ActivitySupportingDocsSubmission from "./pages/activity-config/SupportingDocsSubmission/SupportingDocsSubmission";
import Activityfrequency from "./pages/activity-config/Activityfrequency/ActivityFreq";
import ActivityNotAllowedICD from "./pages/activity-config/ActivityNotAllowedICD/ActivityNotAllowedICD";
import ActivityBundle from "./pages/activity-config/ActivityBundle/ActivityBundle";
import ClaimValidation from "./pages/Validation/ClaimValidation/ClaimValidation";
import BulkClaimValidation from "./pages/Validation/BulkClaim/BulkClaim";
import ClaimValidationHistory from "./pages/Validation/ValidationHistory/ValidationHistory";
import CompanySelector from "./pages/UserManagment/Company/CompanySelector";
import CompanyPagesControl from "./pages/UserManagment/CompanyPagesControl/CompanyPagesControl";
import { isPageAllowed, getSelectedCompanyId } from "./utils/pagePermissions";
import EmployeeManagement from "./pages/UserManagment/EmployeeManagement/EmployeeManagement";
import AuditLogPage from "./pages/UserManagment/Auditlog/Auditlog";

const theme = {
  token: {
    colorPrimary: "#1890ff",
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    colorError: "#ff4d4f",
    colorInfo: "#1890ff",
    borderRadius: 8,
    fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
  },
};

const ADMIN_ROLES = ["admin", "system admin", "system_admin"];
const checkIsAdmin = (role: string) =>
  ADMIN_ROLES.includes(role?.toLowerCase()?.trim() ?? "");

const PageGuard = ({
  children,
  pageKey,
}: {
  children: React.ReactNode;
  pageKey: string;
}) => {
  if (!sessionStorage.getItem("accessToken"))
    return <Navigate to="/login" replace />;

  const companyId = getSelectedCompanyId();
  const allowed = isPageAllowed(pageKey, companyId);

  if (allowed) return <>{children}</>;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "calc(100vh - 64px)",
      }}
    >
      <Result
        status="403"
        title="Access Denied"
        subTitle="This feature has been disabled for your company by the system administrator."
      />
    </div>
  );
};

/* ────────────────────────────────────────────────
   ProtectedRoute
──────────────────────────────────────────────── */
const ProtectedRoute = () => {
  const token = sessionStorage.getItem("accessToken");
  const userStr = sessionStorage.getItem("user");
  if (!token || !userStr) return <Navigate to="/login" replace />;

  const { role } = JSON.parse(userStr);
  return checkIsAdmin(role) ? <AdminLayout /> : <DashboardLayout />;
};

/* ────────────────────────────────────────────────
   Root redirect
──────────────────────────────────────────────── */
const RootRedirect = () => {
  const userStr = sessionStorage.getItem("user");
  const companyId = getSelectedCompanyId();

  if (userStr) {
    const { role } = JSON.parse(userStr);
    if (checkIsAdmin(role) && !companyId) {
      return <Navigate to="/admin/companies" replace />;
    }
  }
  return <Navigate to="/dashboard" replace />;
};

const App: React.FC = () => (
  <ConfigProvider theme={theme}>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={<ProtectedRoute />}>
          {/* Admin-only routes (no PageGuard needed) */}
          <Route path="admin/companies" element={<CompanySelector />} />
          <Route path="admin/pages-control" element={<CompanyPagesControl />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/employees" element={<EmployeeManagement />} />
          <Route path="audit-log" element={<AuditLogPage />} />

          {/* Root redirect */}
          <Route index element={<RootRedirect />} />

          {/* Pages with guard */}
          <Route
            path="dashboard"
            element={
              <PageGuard pageKey="dashboard">
                <Dashboard />
              </PageGuard>
            }
          />

          <Route
            path="code-groups/medicalicd"
            element={
              <PageGuard pageKey="medicalicd">
                <Medicalicd />
              </PageGuard>
            }
          />
          <Route
            path="code-groups/medicalactivity"
            element={
              <PageGuard pageKey="medicalactivity">
                <MedicalActivity />
              </PageGuard>
            }
          />
          <Route
            path="code-groups/customicd"
            element={
              <PageGuard pageKey="customicd">
                <CustomIcd />
              </PageGuard>
            }
          />
          <Route
            path="code-groups/customactivity"
            element={
              <PageGuard pageKey="customactivity">
                <CustomActivity />
              </PageGuard>
            }
          />

          <Route
            path="icd-not-covered"
            element={
              <PageGuard pageKey="icd-not-covered">
                <IcdNotCovered />
              </PageGuard>
            }
          />
          <Route
            path="icd-not-allowed-principal-diagnosis"
            element={
              <PageGuard pageKey="icd-not-allowed-principal">
                <IcdNotAllowedPrincipalDiagnosis />
              </PageGuard>
            }
          />
          <Route
            path="icd-config/age-gender"
            element={
              <PageGuard pageKey="icd-age-gender">
                <ICDAgeGender />
              </PageGuard>
            }
          />

          <Route
            path="activity-not-covered"
            element={
              <PageGuard pageKey="activity-not-covered">
                <ActivityNotCovered />
              </PageGuard>
            }
          />
          <Route
            path="activity-age-gender"
            element={
              <PageGuard pageKey="activity-age-gender">
                <ActivityAgeGender />
              </PageGuard>
            }
          />
          <Route
            path="activities-not-allowed"
            element={
              <PageGuard pageKey="activities-not-allowed">
                <ActivitiesNotAllowed />
              </PageGuard>
            }
          />
          <Route
            path="activities-allowed"
            element={
              <PageGuard pageKey="activities-allowed">
                <ActivitiesAllowed />
              </PageGuard>
            }
          />
          <Route
            path="activity-not-allowed-icd"
            element={
              <PageGuard pageKey="activity-not-allowed-icd">
                <ActivityNotAllowedICD />
              </PageGuard>
            }
          />
          <Route
            path="activity-pre-approvals"
            element={
              <PageGuard pageKey="activity-pre-approvals">
                <ActivityPreApprovals />
              </PageGuard>
            }
          />
          <Route
            path="activity-supporting-docs-submission"
            element={
              <PageGuard pageKey="activity-supporting-docs">
                <ActivitySupportingDocsSubmission />
              </PageGuard>
            }
          />
          <Route
            path="activity-frequency"
            element={
              <PageGuard pageKey="activity-frequency">
                <Activityfrequency />
              </PageGuard>
            }
          />
          <Route
            path="activity-bundle"
            element={
              <PageGuard pageKey="activity-bundle">
                <ActivityBundle />
              </PageGuard>
            }
          />

          <Route
            path="tpa-payer"
            element={
              <PageGuard pageKey="tpa-payer">
                <TpaPayerPage />
              </PageGuard>
            }
          />
          <Route
            path="network-plan"
            element={
              <PageGuard pageKey="network-plan">
                <NetworkPlanPage />
              </PageGuard>
            }
          />
          <Route
            path="policy"
            element={
              <PageGuard pageKey="policy">
                <PolicyPage />
              </PageGuard>
            }
          />
          <Route
            path="specialty"
            element={
              <PageGuard pageKey="specialty">
                <SpecialtyPage />
              </PageGuard>
            }
          />
          <Route
            path="doctor"
            element={
              <PageGuard pageKey="doctor">
                <DoctorPage />
              </PageGuard>
            }
          />

          <Route
            path="claim-validation"
            element={
              <PageGuard pageKey="claim-validation">
                <ClaimValidation />
              </PageGuard>
            }
          />
          <Route
            path="bulk-claim-validation"
            element={
              <PageGuard pageKey="bulk-claim-validation">
                <BulkClaimValidation />
              </PageGuard>
            }
          />
          <Route
            path="claim-validation-history"
            element={
              <PageGuard pageKey="claim-validation-history">
                <ClaimValidationHistory />
              </PageGuard>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </ConfigProvider>
);

export default App;
